// ============================================================
// COMMISSION TRACKING PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Percent, Download } from 'lucide-react';
import { Table, Button, SearchInput, Pagination, StatCard } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getCommissionData } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatCurrency, downloadCSV } from '../utils/helpers';

const CommissionTracking = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const normalize = (items) => items.map(d => ({
    ...d,
    restaurant: d.restaurant || d.restaurantName || '',
    orders: d.orders || d.totalOrders || 0,
  }));

  useEffect(() => {
    adminApi.getCommissionData(getCommissionData)
      .then(res => { setAll(normalize(Array.isArray(res) ? res : (res?.items || []))); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['restaurant']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  const chartData = all.slice(0, 10).map(d => ({ name: d.restaurant.split(' ')[0], commission: d.commissionAmount, rate: d.commissionRate }));

  const columns = [
    { key: 'restaurant', label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'period', label: 'Period' },
    { key: 'orders', label: 'Orders' },
    { key: 'commissionRate', label: 'Rate', render: v => <span style={{ fontWeight: 700, color: '#E85D04' }}>{v}%</span> },
    { key: 'grossRevenue', label: 'GMV', render: v => formatCurrency(v) },
    { key: 'commissionAmount', label: 'Commission', render: v => <b style={{ color: '#10B981' }}>{formatCurrency(v)}</b> },
    { key: 'platformFee', label: 'Platform Fee', render: v => formatCurrency(v) },
    {
      key: 'status', label: 'Status',
      render: v => <span className="admin-badge" style={{ color: v === 'paid' ? '#10B981' : '#F59E0B', background: v === 'paid' ? '#D1FAE5' : '#FEF3C7' }}>{v}</span>,
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Commission Tracking</h1>
          <p className="admin-page-subtitle">Monitor commission earnings per restaurant</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'commission')}>Export</Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 24 }}>
        <StatCard label="Total Commission" value={formatCurrency(all.reduce((s, d) => s + d.commissionAmount, 0))} icon={<Percent size={18} />} color="#E85D04" />
        <StatCard label="Avg Commission Rate" value={`${(all.reduce((s, d) => s + d.commissionRate, 0) / (all.length || 1)).toFixed(1)}%`} icon={<Percent size={18} />} color="#3B82F6" />
        <StatCard label="Paid Commissions" value={all.filter(d => d.status === 'paid').length} icon={<Percent size={18} />} color="#10B981" />
      </div>

      <div className="admin-card" style={{ marginBottom: 24 }}>
        <div className="admin-card-header"><h3 className="admin-card-title">Top 10 Restaurants by Commission</h3></div>
        <div className="admin-card-body">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--admin-border)" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--admin-text-secondary)' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}K`} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: 'var(--admin-text-secondary)' }} width={80} />
              <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', borderRadius: 8 }} />
              <Bar dataKey="commission" fill="#E85D04" radius={[0, 4, 4, 0]} name="Commission" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search by restaurant..." />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
      </div>
    </div>
  );
};

export default CommissionTracking;
